import React, { ChangeEvent, useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  TextField,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  MenuItem,
  Select,
  SelectChangeEvent,
  Theme,
  useTheme,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "react-phone-input-2/lib/bootstrap.css";
import * as Yup from "yup";
import { Field, useFormik } from "formik";
import {
  useLazyGetTeachingDetailsByIdQuery,
  usePostTutorTeachingDetailsMutation,
} from "../../services/tutor";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody, SubjectBody } from "../../types/General";
import {
  errorToast,
  getFromStorage,
  STORAGE_KEYS,
  successToast,
} from "../../helpers";
import moment from "moment";
import { usePutTeachingDetailsMutation } from "../../services/tutor";
import DeleteIcon from "@mui/icons-material/Delete";
import Multiselect from "multiselect-react-dropdown";
import { useLazyGetIdProofByIdQuery } from "../../services/documentation";
import { isNumber, isString } from "../../utils/validation";
import Loader from "../../helpers/Loader";
import {
  useGetAllCategoryQuery,
  useGetCatSubListQuery,
  useLazyGetAllSubjectQuery,
  useLazyGetCatSubListQuery,
} from "../../services/category";
import { LISTING } from "../../helpers/constants/enums";
import { classOptions } from "../../pages/manageClass/add";

enum CurriculumType {
  Cambridge = 1,
  IB = 2,
  National = 3,
  American = 4,
  Others = 5,
}

enum TeachingStyle {
  VisualLearning = 1,
  AuditoryLearning = 2,
  ReadingAndWriting = 3,
  IntegratedApproach = 4,
  Others = 5,
}

const curriculumOptions = [
  { value: CurriculumType.Cambridge, label: "Cambridge Curriculum" },
  { value: CurriculumType.IB, label: "IB Curriculum" },
  { value: CurriculumType.National, label: "National Curriculum" },
  { value: CurriculumType.American, label: "American Curriculum" },
  { value: CurriculumType.Others, label: "Others" },
];

const teachingStyleOptions = [
  { value: TeachingStyle.VisualLearning, label: "Visual Learning" },
  { value: TeachingStyle.AuditoryLearning, label: "Auditory Learning" },
  { value: TeachingStyle.ReadingAndWriting, label: "Reading and Writing" },
  { value: TeachingStyle.IntegratedApproach, label: "Integrated Approach" },
  { value: TeachingStyle.Others, label: "Others" },
];

// const classOptions = [
//   { label: "Pre primary(Kg/Foundation)", value: "0" },
//   { label: "Primary", value: "1" },
//   { label: "Middle school (O-level)", value: "2" },
//   { label: "High school (A-level)", value: "3" },
//   { label: "College", value: "4" },
//   { label: "Other", value: "5" },
// ];

const experienceOptions = [
  { label: "1 year", value: "1" },
  { label: "2 years", value: "2" },
  { label: "3 years", value: "3" },
  { label: "4 years", value: "4" },
  { label: "5+ years", value: "5" },
];

const languageOptions = [
  { label: "English", value: "1" },
  { label: "Arabic", value: "2" },
  { label: "Both", value: "3" },
];

const TeachingDetailsSchema = Yup.object().shape({
  totalTeachingExperience: Yup.string().required("Experience is required"),
  classNumber: Yup.array().min(1, "At least one class is required"),
  curriculum: Yup.array().min(1, "At least one curriculum is required"),
  teachingStyle: Yup.array().min(1, "At least one teaching style is required"),
  teachingLanguage: Yup.string().required("Teaching language is required"),
  category: Yup.string().required("Category is required"),
  subjects: Yup.array().min(1, "At least one subject is required"),
  price: Yup.number()
    .max(10000000, "Price cannot be more than 10000000")
    .required("Price is required"),
  startTime: Yup.string().required("Start time is required"),
  endTime: Yup.string().required("End time is required"),
});

function TeachingDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { _id } = useParams();
  const tId = getFromStorage(STORAGE_KEYS.tutorId);
  const { state } = location;

  const [detailsById, { isLoading }] = useLazyGetTeachingDetailsByIdQuery();
  const [AddTeachingDetails] = usePostTutorTeachingDetailsMutation();
  const [getAllSubject] = useLazyGetCatSubListQuery();
  const { data: categoryData } = useGetCatSubListQuery({
    type: LISTING.CATEGORY,
  });

  const [subjectData, setSubjectData] = useState<any[]>([]);
  const [responseClasses, setResponseClasses] = useState<any[]>([]);
  const [showOtherCurriculum, setShowOtherCurriculum] = useState(false);

  const formik = useFormik({
    initialValues: {
      tutorId: _id || tId || "",
      totalTeachingExperience: "",
      classNumber: [] as string[],
      curriculum: [] as number[],
      curriculumOther: "",
      teachingStyle: [] as number[],
      teachingLanguage: "",
      category: "",
      subjects: [] as string[],
      price: "",
      startTime: "",
      endTime: "",
    },
    validationSchema: TeachingDetailsSchema,
    onSubmit: async (values) => {
      try {
        const startTimeUtc = values.startTime
          ? moment.utc(values.startTime, "HH:mm").format()
          : "";
        const endTimeUtc = values.endTime
          ? moment.utc(values.endTime, "HH:mm").format()
          : "";

        const body = {
          tutorId: values.tutorId,
          totalTeachingExperience: Number(values.totalTeachingExperience),
          curriculum: values.curriculum,
          curriculumOther: values.curriculum.includes(CurriculumType.Others)
            ? values.curriculumOther
            : "",
          classes: values.classNumber,
          // classes: [1],
          categoryId: values.category,
          teachingStyle: values.teachingStyle,
          price: values.price,
          startTime: startTimeUtc,
          endTime: endTimeUtc,
          subjectIds: values.subjects,
          teachingLanguage: Number(values.teachingLanguage),
        };

        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await AddTeachingDetails(encryptedBody).unwrap();

        if (response?.statusCode === 200) {
          successToast(
            `Teaching details ${_id ? "updated" : "added"} successfully`
          );
          navigate(_id ? `/manage-tutors/edit/${_id}` : `/manage-tutors/add`, {
            state: { tab: 3 },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
  });

  const fetchDataById = async (id: any) => {
    try {
      const response = await detailsById({ userId: id }).unwrap();
      if (response.statusCode === 200) {
        const teachingDetails = response.data[0];

        formik.setValues({
          tutorId: id,
          totalTeachingExperience:
            teachingDetails?.totalTeachingExperience?.toString() || "",
          classNumber: teachingDetails?.classes?.map(String) || [],
          curriculum: teachingDetails?.curriculum || [],
          curriculumOther: teachingDetails?.curriculumOther || "",
          teachingStyle: teachingDetails?.teachingStyle || [],
          teachingLanguage: teachingDetails?.teachingLanguage?.toString() || "",
          category: teachingDetails?.categoryId?._id || "",
          subjects:
            teachingDetails?.subjectIds?.map((item: any) => item?._id) || [],
          price: teachingDetails?.price || "",
          startTime: teachingDetails?.startTime?.substring(11, 16) || "",
          endTime: teachingDetails?.endTime?.substring(11, 16) || "",
        });

        setResponseClasses(teachingDetails?.classes || []);
        setShowOtherCurriculum(
          teachingDetails?.curriculum?.includes(CurriculumType.Others) || false
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSubjects = async () => {
    if (!formik.values.category) return;

    try {
      const response = await getAllSubject({
        type: LISTING.SUBJECT,
        categoryId: formik.values.category,
      }).unwrap();

      if (response.statusCode === 200) {
        setSubjectData(
          response?.data?.map((item: any) => ({
            value: item?._id,
            label: item?.name,
          }))
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (_id || tId) {
      fetchDataById(_id ? _id : tId);
    }
  }, [_id, tId]);

  useEffect(() => {
    if (formik.values.category) {
      fetchSubjects();
    }
  }, [formik.values.category]);

  useEffect(() => {
    setShowOtherCurriculum(
      formik.values.curriculum.includes(CurriculumType.Others)
    );
  }, [formik.values.curriculum]);

  const getLevelName = (value: string) => {
    const option = classOptions.find((opt) => opt.value === value);
    return option ? option.label : "";
  };

  return (
    <div className="main_loyout">
      <div className="dashboard">
        {state?.type === "edit" ? (
          <h1 className="mn_hdng">Edit Tutor</h1>
        ) : (
          <h1 className="mn_hdng">Teaching Details</h1>
        )}
        <Button
          className="btn btn_primary"
          onClick={() => navigate("/manage-tutors")}
        >
          Back
        </Button>
      </div>
      <Loader isLoad={isLoading} />
      <Card className="cards">
        <form onSubmit={formik.handleSubmit}>
          <CardContent sx={{ p: 1 }}>
            <Grid container spacing={2}>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  <Typography className="custom_label">
                    Total Teaching Experience
                  </Typography>
                  <Select
                    className="select_div"
                    id="totalTeachingExperience"
                    name="totalTeachingExperience"
                    value={formik.values.totalTeachingExperience}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select
                    </MenuItem>
                    {experienceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.totalTeachingExperience &&
                    formik.errors.totalTeachingExperience && (
                      <h6 className="err_msg">
                        {formik.errors.totalTeachingExperience}
                      </h6>
                    )}
                </FormControl>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  <Typography className="custom_label">Select Class</Typography>
                  <Select
                    className="select_div"
                    id="classNumber"
                    name="classNumber"
                    multiple
                    value={formik.values.classNumber}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "classNumber",
                        e.target.value as string[]
                      );
                    }}
                    onBlur={formik.handleBlur}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) return <span>Select</span>;
                      return selected.map(getLevelName).join(", ");
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select
                    </MenuItem>
                    {classOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.classNumber && formik.errors.classNumber && (
                    <h6 className="err_msg">{formik.errors.classNumber}</h6>
                  )}
                </FormControl>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  <Typography className="custom_label">
                    Select Curriculum
                  </Typography>
                  <Select
                    className="select_div"
                    id="curriculum"
                    name="curriculum"
                    multiple
                    value={formik.values.curriculum}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "curriculum",
                        e.target.value as number[]
                      );
                    }}
                    onBlur={formik.handleBlur}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) return <span>Select</span>;
                      return selected
                        .map(
                          (value) =>
                            curriculumOptions.find(
                              (option) => option.value === value
                            )?.label || ""
                        )
                        .join(", ");
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select
                    </MenuItem>
                    {curriculumOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.curriculum && formik.errors.curriculum && (
                    <h6 className="err_msg">{formik.errors.curriculum}</h6>
                  )}
                </FormControl>
              </Grid>

              {showOtherCurriculum && (
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <Typography className="custom_label">
                    Other Curriculum
                  </Typography>
                  <TextField
                    type="text"
                    name="curriculumOther"
                    variant="outlined"
                    fullWidth
                    placeholder="Specify Other Curriculum"
                    className="text_field"
                    value={formik.values.curriculumOther}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
              )}

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  <Typography className="custom_label">
                    Select Teaching Style
                  </Typography>
                  <Select
                    className="select_div"
                    id="teachingStyle"
                    name="teachingStyle"
                    multiple
                    value={formik.values.teachingStyle}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "teachingStyle",
                        e.target.value as number[]
                      );
                    }}
                    onBlur={formik.handleBlur}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) return <span>Select</span>;
                      return selected
                        .map(
                          (value) =>
                            teachingStyleOptions.find(
                              (option) => option.value === value
                            )?.label || ""
                        )
                        .join(", ");
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select
                    </MenuItem>
                    {teachingStyleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.teachingStyle &&
                    formik.errors.teachingStyle && (
                      <h6 className="err_msg">{formik.errors.teachingStyle}</h6>
                    )}
                </FormControl>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  <Typography className="custom_label">
                    Teaching Language
                  </Typography>
                  <Select
                    className="select_div"
                    id="teachingLanguage"
                    name="teachingLanguage"
                    value={formik.values.teachingLanguage}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select
                    </MenuItem>
                    {languageOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.teachingLanguage &&
                    formik.errors.teachingLanguage && (
                      <h6 className="err_msg">
                        {formik.errors.teachingLanguage}
                      </h6>
                    )}
                </FormControl>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">Price</Typography>
                <TextField
                  type="text"
                  name="price"
                  className="text_field"
                  variant="outlined"
                  fullWidth
                  placeholder="Price"
                  value={formik.values.price}
                  onChange={(e) => {
                    if (e.target.value === " ") return;
                    if (isNumber(e.target.value)) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={formik.touched.price && formik.errors.price}
                />
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">Start Time</Typography>
                <TextField
                  type="time"
                  name="startTime"
                  variant="outlined"
                  fullWidth
                  placeholder="Start time"
                  className="text_field"
                  value={formik.values.startTime}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.startTime && Boolean(formik.errors.startTime)
                  }
                  helperText={
                    formik.touched.startTime && formik.errors.startTime
                  }
                />
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">End Time</Typography>
                <TextField
                  type="time"
                  name="endTime"
                  variant="outlined"
                  fullWidth
                  placeholder="End time"
                  className="text_field"
                  value={formik.values.endTime}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.endTime && Boolean(formik.errors.endTime)
                  }
                  helperText={formik.touched.endTime && formik.errors.endTime}
                />
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  <Typography className="custom_label">Category</Typography>
                  <Select
                    className="select_div"
                    id="category"
                    name="category"
                    value={formik.values.category}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Category
                    </MenuItem>
                    {categoryData?.data?.map((item: any) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.category && formik.errors.category && (
                    <h6 className="err_msg">{formik.errors.category}</h6>
                  )}
                </FormControl>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  <Typography className="custom_label">
                    Select Subject
                  </Typography>
                  <Select
                    className="select_div"
                    id="subjects"
                    name="subjects"
                    multiple
                    disabled={!formik.values.category}
                    value={formik.values.subjects}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "subjects",
                        e.target.value as string[]
                      );
                    }}
                    onBlur={formik.handleBlur}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) return <span>Select</span>;
                      return selected
                        .map(
                          (value) =>
                            subjectData.find((option) => option.value === value)
                              ?.label || ""
                        )
                        .join(", ");
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select
                    </MenuItem>
                    {subjectData.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.subjects && formik.errors.subjects && (
                    <h6 className="err_msg">{formik.errors.subjects}</h6>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            <div className="form_btn">
              <Button size="large" type="submit" className="btn btn_primary">
                Submit
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}

export default TeachingDetails;
